import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  IconButton,
  TextField,
  Grid,
  Tooltip
} from '@material-ui/core';
import { Delete, BorderAllRounded } from '@material-ui/icons';
import PropTypes from 'prop-types';
import RuleGroup from './RuleGroup';
import getConditionTypeHelpers from '../helpers/conditionTypesHelpers';
import Conditions from './Conditions';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { SelectInput } from 'components';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
const removeIconStyles = t => ({
  removeButton: {
    marginRight: t.spacing(-1),
    marginTop: t.spacing(0.75)
  },
  removeIcon: {
    fill: 'rgba(255, 0, 0, 0.9)'
  },
  groupLabel: {
    backgroundColor: '#E5E5E5',
    color: 'rgba(0, 0, 0, 0.87)',
    lineHeight: '20px',
    padding: '8px 16px'
    }
});

const useRuleStyles = makeStyles(theme => {
  return {
    ...removeIconStyles(theme),
    container: {
      '& > div': {
        marginBottom: theme.spacing(0.5),
        marginTop: theme.spacing(0.5) ,
      },
    },
    valueGridItem: {
    flex: 'auto'
    },
    root: {
      flexGrow: 1
    },
    button: {
      margin: theme.spacing(1)
    },
    deleteButton: {
      color: theme.palette.error.main
    },
    containerMargin: {
      marginLeft: theme.spacing(1)
    },
    containerAndMargin: {
      marginLeft: theme.spacing(3),
      textAlign: 'left',
      fontWeight: 'bold'
    },
    selectContainer: {
      display: 'inline',
      minWidth: '14rem',
      margin: theme.spacing(1)
    },
    autoCompleteContainer: {
      display: 'inline',
      minWidth: '14rem',
      margin: theme.spacing(1),
      marginTop: theme.spacing(2)
    },
    buttonContainer: {
      display: 'inline',
      margin: theme.spacing(1)
    },
    ruleError: {
      color: theme.palette.error.main,
      paddingTop: '13px'
    },
    dateField: {
      width: '100%',
      marginTop: '0px'
    },
    templateButtonError: {
      paddingLeft: '19px',
      color: theme.palette.error.main
    },
    ruleDivider: {
      marginTop: '10px',
      marginBottom: '10px'
    },
    groupCard: {
      marginTop: '10px',
      marginBottom: '10px'
    },
    dataFrame: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'space-between',
    }
  };
});

const Rule = props => {
  const classes = useRuleStyles();

  const { id, level, position, rule, context, template, isReadOnly ,handleDragEnd,handleDragStart,baseRuleId} = props;
  const { conditionOperator, subConditionExpressions } = rule;
  const { dispatch } = context;
  const testId = `${level}-${position}`;
  const { thisTemplate } = template;

  const [flattenedEventMetaData, setFlattenedEventMetaData] = useState([]);

  useEffect(() => {
    let eventFields = [];
    thisTemplate.eventMetaData.map(data =>
      eventFields.push(...data.schemaConditionAttributes.filter(f => !f.metaAttribute).map(f => ({ ...f, schemaType: data.schemaType })).sort((a, b) => a?.name.localeCompare(b?.name)))
    );
    setFlattenedEventMetaData(eventFields);
  }, [thisTemplate.eventMetaData]);

  const {
    filterOperatorsByCondition,
    getConditionType,
    getDataFrameAttributes,
    getDataFrameConditionType
  } = getConditionTypeHelpers(flattenedEventMetaData);

  const getOption = option => {
    if (typeof option === 'object') {
      return option.name;
    } else {
      return '';
    }
  };

  const operatorOptions = {
    list: filterOperatorsByCondition(rule.dataFrameConditionData?.dataFrameAttribute, rule.conditionAttribute),
    keyId: 'displayName',
    valueId: 'type',
    displayValueId: 'displayName'
  };

  const sortedDataFrameAttributes = [...(rule.dataFrameAttribute?.attributes || [])].sort((a, b) => a?.displayName.localeCompare(b?.displayName));

  const dataFrameAttributeOptions = {
    list: sortedDataFrameAttributes,
    keyId: 'displayName',
    valueId: 'conditionAttribute',
    displayValueId: 'displayName'
  };
  
  return conditionOperator ? (
    <Card className={classes.groupCard}>  
     <CardContent>
        <RuleGroup
          conditionOperator={conditionOperator}
          id={id}
          level={level + 1}
          subConditionExpressions={subConditionExpressions}
          context={context}
          template={template}
          isReadOnly={isReadOnly}
          ruleParent={rule}
          parentRulePosition={position}
                  />
      </CardContent> 
    </Card>
  ) : (
    <Grid  
      container
      id="group"
      className={classes.container}
      data-testid={`rule-${testId}`}
      spacing={2}>
      <Grid
        container
        className={classes.containerMargin}
        key={`Rule_${rule.id}`}>
        <Grid item className={classes.autoCompleteContainer}>
          <Autocomplete
            classes={{ groupLabel: classes.groupLabel }}
            name="conditionAttribute"
            id="condition-Attribute"
            size="small"
            onChange={(event, values) => {
              if (values != null) {
                let value = values.key;
                let conditionDataType = getConditionType(value);
                let schemaType = values.schemaType;
                if (values.attributeFieldType === 'dataFrame') {
                  let dataFrameAttribute = getDataFrameAttributes(value);
                  dispatch({
                    type: 'setDataFrameConditionAttribute',
                    id,
                    value,
                    conditionDataType,
                    schemaType,
                    dataFrameAttribute
                  });
                }
                else {
                  dispatch({
                    type: 'setConditionAttribute',
                    id,
                    value,
                    conditionDataType,
                    schemaType
                  });
                }
              }
            }}
            disabled={isReadOnly()}
            options={flattenedEventMetaData}
            groupBy={option => option.productName}
            value={
              flattenedEventMetaData.find(
                v => v.key == (rule?.dataFrameConditionData?.dataFrameAttribute ? rule?.dataFrameConditionData?.dataFrameAttribute : rule.conditionAttribute)
              ) || ''
            }
            renderOption={option => (
              <div className={classes.dataFrame}>
                <div>
                  {option.name}
                </div>
                {option?.attributeFieldType === 'dataFrame' && (
                  <Tooltip title={'Dataframe field'}>
                    <BorderAllRounded />
                  </Tooltip>
                )}
              </div>
            )}
            getOptionLabel={getOption}
            style={{ width: 229 }}
            renderInput={params => (
              <TextField
                {...params}
                label="Select Query Parameter"
                variant="outlined"
                required
              />
            )}
          />
        </Grid>
        {(rule.dataFrameConditionData && rule.dataFrameConditionData?.dataFrameAttribute !== '') && (
          <Grid item className={classes.selectContainer}>
            <SelectInput
              fullWidth
              label="Select Dataframe Field"
              margin="dense"
              name="dataFrameAttribute"
              disabled={isReadOnly()}
              onChange={event => {
                let value = event.target.value;
                let conditionDataType = getDataFrameConditionType(rule.dataFrameConditionData?.dataFrameAttribute, value);
                dispatch({ 
                  type: 'setDataFrameFieldAttribute',
                  id,
                  value,
                  conditionDataType
                });
              }}
              required
              value={rule.conditionAttribute || ''}
              options={dataFrameAttributeOptions}
            />
          </Grid>
        )}
        {rule.conditionAttribute !== '' && (
          <Grid item className={classes.selectContainer}>
            <SelectInput
              fullWidth
              label="Select Operator"
              margin="dense"
              name="condition"
              disabled={isReadOnly()}
              onChange={event => {
                let value = event.target.value;
                dispatch({ type: 'setCondition', id, value });
              }}
              required
              value={rule.condition || ''}
              options={operatorOptions}
            />
          </Grid>
        )}
        <Conditions
          id={id}
          test-id={testId}
          context={context}
          template={template}
          rule={rule}
          isReadOnly={isReadOnly}
        />
        {!isReadOnly() && (
          <>
          <Grid item className={classes.buttonContainer}>
            <Tooltip title="Delete">
              <IconButton
                className={classes.removeButton}
                data-testid={`rule-${testId}-remove`}
                size="small"
                onClick={() => {
                  dispatch({ type: 'removeNode', id });
                }}>
                <Delete className={classes.removeIcon} />
              </IconButton>
            </Tooltip>
          </Grid>
              <Grid item className={classes.buttonContainer} draggable={true}  
                onDragStart={(event)=>{event.stopPropagation();handleDragStart(rule, position,baseRuleId)}}
                onDragEnd={(e) => {e.stopPropagation();handleDragEnd()}}>
                <Tooltip title="Drag">
                  <IconButton
                    className={classes.removeButton}
                    data-testid={`rule-${testId}-remove`}
                    size="small"
                    title='Drag to reorder'
                    style={{cursor: 'move'}}
                    >
                    <DragIndicatorIcon style={{color:'#43a047'}} />
                  </IconButton>
                </Tooltip>
              </Grid>
             </>
        )}
      </Grid>
    </Grid>
  );
};

Rule.propTypes = {
  id: PropTypes.number.isRequired,
  level: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  rule: PropTypes.object.isRequired
};

export default Rule;
